import React, { useContext, useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation } from 'convex/react';
import { api } from '../../../../convex/_generated/api';
import { Button } from '../../../../catalyst-components/button';
import { Field, Label } from '../../../../catalyst-components/fieldset';
import { Input } from '../../../../catalyst-components/input';
import { Select } from '../../../../catalyst-components/select';
import { Alert, AlertActions, AlertDescription, AlertTitle } from '../../../../catalyst-components/alert';
import { Id } from '../../../../convex/_generated/dataModel';
import { Textarea } from '../../../../catalyst-components/textarea';
import { Text } from '../../../../catalyst-components/text';
import { Switch } from '../../../../catalyst-components/switch';
import { FieldGroup } from '../../../../catalyst-components/fieldset';
import { useAuthToken } from '../../../../shared/hooks/useAuthToken';
import { BaseContext } from '../../../../shared/base-context';
import { ChatSessionsList } from './components/chat-sessions-list';
import { MultipleChoiceQuestion } from '../../../../shared/models';
import { MultipleChoiceQuestionsList } from './components/multiple-choice-questions-list';
import { Spinner } from '../../../../catalyst-components/spinner';
import { AcornIcon } from '../../../../shared/components/AcornIcon';
import { injectHeaderAndFooter } from '../../../../shared/helpers/utils';
import { useStableQuery } from '../../../../shared/hooks/useStableQuery';
import { usePersistedForm } from '../../../../shared/hooks/usePersistedForm';

const convexSiteUrl = process.env.REACT_APP_CONVEX_URL?.replace(
  /\.cloud$/,
  ".site"
);

const defaultResponseSchema = `{
  "name": "response_schema_with_suggested_replies",
  "strict": true,
  "schema": {
    "type": "object",
    "properties": {
      "response": {
        "type": "string",
        "description": "Your response."
      },
      "completed": {
        "type": "boolean",
        "description": "A flag indicating whether the task is completed."
      },
      "answers": {
        "type": "array",
        "description": "A list of answers related to the task.",
        "items": {
          "type": "string"
        }
      }
    },
    "required": [
      "response",
      "completed",
      "answers"
    ],
    "additionalProperties": false
  }
}
`

export const AddAssistant: React.FC = () => {
  const { assistantId } = useParams();
  const navigate = useNavigate();

  const [formState, updateFormState] = usePersistedForm(`assistant-form-${assistantId || 'new'}`, {
    name: '',
    display_name: '',
    type: '' as Id<"assistant_types"> | '',
    greeting: '',
    multipleChoice: false,
    prompt: '',
    isDraft: true,
    showExitButton: false,
    exitButtonCaption: '',
    showYourChatButton: false,
    yourChatButtonCaption: '',
    description: '',
    temperature: 0.2,
    isCore: false,
    systemResponseSchema: defaultResponseSchema,
    multipleChoiceQuestions: [] as MultipleChoiceQuestion[],
    multipleChoiceSignOff: '',
    header: '',
    footer: '',
    midpoint: '',
    useScoring: false,
    useSummary: false,
    showUploadResumeButton: false,
    uploadResumeButtonCaption: '',
    useTools: false,
    xpReward: 50, // Default XP reward for completing a session
    acornReward: 5, // Default acorn reward for completing a session
  });

  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [success, setSuccess] = useState(false);
  const [newAssistantId, setNewAssistantId] = useState<Id<"assistants"> | null>(null);
  const [showInProgressAlert, setShowInProgressAlert] = useState(false);
  const [request, setRequest] = useState('');
  const [generatedContent, setGeneratedContent] = useState('');
  const [generatedContentId, setGeneratedContentId] = useState<Id<"ai_generated_content"> | null>(null);
  const [defaultHeader, setDefaultHeader] = useState("");
  const [defaultMidpoint, setDefaultMidpoint] = useState("");
  const [defaultFooter, setDefaultFooter] = useState("");
  const [defaultBody, setDefaultBody] = useState("");
  const [isInitialized, setIsInitialized] = useState(false);
  const [forceReload, setForceReload] = useState(false);
  // Track if we've already loaded the assistant data into the form to prevent Convex refresh from overwriting edits
  const hasLoadedAssistant = useRef(false);

  const token = useAuthToken();
  const addAssistant = useMutation(api.assistants.addAssistant);
  const updateAssistant = useMutation(api.assistants.updateAssistant);
  const assistantTypes = useStableQuery(api.assistant_types.list) || [];
  const { convexUserId } = useContext(BaseContext);
  const generateContent = useMutation(api.ai_generated_content.create);
  const generatedContentResponse = useStableQuery(api.ai_generated_content.getById, generatedContentId ? { id: generatedContentId as Id<"ai_generated_content"> } : 'skip');
  const assistant = useStableQuery(api.assistants.getById, assistantId ? { id: assistantId as Id<"assistants"> } : 'skip');
  const adminMetadata = useStableQuery(api.assistant_admin_metadata.get);

  useEffect(() => {
    // Only initialize defaults if we haven't done so yet
    if (!isInitialized && adminMetadata) {
      setDefaultHeader(adminMetadata.header || "");
      setDefaultMidpoint(adminMetadata.midpoint || "");
      setDefaultFooter(adminMetadata.footer || "");
      setDefaultBody(adminMetadata.body || "");
      setIsInitialized(true);
    }

    // Load assistant data only once on initial load, or when forceReload is explicitly set (after save)
    // This prevents Convex subscription refreshes from overwriting user edits mid-editing
    if (assistant && (!hasLoadedAssistant.current || forceReload)) {
      hasLoadedAssistant.current = true;
      updateFormState({
        ...formState,
        name: assistant.name,
        display_name: assistant.display_name || '',
        type: assistant.type as Id<"assistant_types">,
        greeting: assistant.greeting || '',
        multipleChoice: !!assistant.multiple_choice,
        prompt: assistant.prompt || '',
        isDraft: !!assistant.is_draft,
        systemResponseSchema: assistant.system_response_schema || defaultResponseSchema,
        showExitButton: !!assistant.show_exit_button,
        description: assistant.description || '',
        temperature: assistant.temperature || 0.2,
        isCore: !!assistant.is_core,
        exitButtonCaption: assistant.exit_button_caption || '',
        showYourChatButton: !!assistant.show_your_chat_button,
        yourChatButtonCaption: assistant.your_chat_button_caption || '',
        multipleChoiceQuestions: assistant.multiple_choice_questions || [],
        multipleChoiceSignOff: assistant.multiple_choice_sign_off || '',
        header: assistant.header || defaultHeader || '',
        footer: assistant.footer || defaultFooter || '',
        midpoint: assistant.midpoint || defaultMidpoint || '',
        useScoring: !!assistant.use_scoring,
        useSummary: !!assistant.use_summary,
        showUploadResumeButton: !!assistant.show_upload_resume_button,
        uploadResumeButtonCaption: assistant.upload_resume_button_caption || '',
        useTools: !!assistant.use_tools,
        xpReward: assistant.xp_reward ?? 50,
        acornReward: assistant.acorn_reward ?? 5,
      });
      // Reset forceReload flag after updating form
      if (forceReload) {
        setForceReload(false);
      }
    } else if (isInitialized && !assistantId && (!formState.prompt || !formState.header || !formState.footer || !formState.midpoint)) {
      // Only set defaults if we're creating a new assistant AND the form is empty
      // Don't apply defaults if we just created an assistant (newAssistantId exists)
      if (!newAssistantId) {
        updateFormState({
          ...formState,
          prompt: defaultBody,
          showExitButton: true,
          exitButtonCaption: 'Next interaction',
          showYourChatButton: true,
          yourChatButtonCaption: 'Just chat',
          isCore: false,
          header: defaultHeader,
          footer: defaultFooter,
          midpoint: defaultMidpoint,
          useScoring: true,
          useSummary: true,
        });
      }
    }
  }, [assistant, adminMetadata, isInitialized]);

  useEffect(() => {
    if (generatedContentId) {
      setGeneratedContent(generatedContentResponse?.response || '');
    }
  }, [generatedContentId, generatedContentResponse]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formState.type) {
      setAlertMessage('Please select an assistant type.');
      setShowAlert(true);
      setSuccess(false);
      return;
    }

    try {
      setShowInProgressAlert(true);
      let id: Id<"assistants"> | null = assistantId ? assistantId as Id<"assistants"> : null;
      const assistantData = {
        name: formState.name,
        display_name: formState.display_name,
        type: formState.type,
        greeting: formState.greeting,
        multiple_choice: formState.multipleChoice,
        prompt: formState.prompt,
        is_draft: formState.isDraft,
        system_response_schema: formState.systemResponseSchema,
        last_modified_by: convexUserId as Id<"users">,
        show_exit_button: formState.showExitButton,
        temperature: formState.temperature,
        is_core: formState.isCore,
        exit_button_caption: formState.exitButtonCaption,
        show_your_chat_button: formState.showYourChatButton,
        your_chat_button_caption: formState.yourChatButtonCaption,
        multiple_choice_questions: formState.multipleChoiceQuestions,
        header: formState.header,
        description: formState.description,
        footer: formState.footer,
        midpoint: formState.midpoint,
        multiple_choice_sign_off: formState.multipleChoiceSignOff,
        use_scoring: formState.useScoring,
        use_summary: formState.useSummary,
        show_upload_resume_button: formState.showUploadResumeButton,
        upload_resume_button_caption: formState.uploadResumeButtonCaption,
        use_tools: formState.useTools,
        xp_reward: formState.xpReward,
        acorn_reward: formState.acornReward,
      }
      if (id) {
        await updateAssistant({
          id,
          ...assistantData
        });
      } else {
        id = await addAssistant({ ...assistantData });
        setNewAssistantId(id);
      }

      const requestBody = {
        id: assistant?.openai_assistant_id,
        instructions: injectHeaderAndFooter(formState.prompt, formState.header, formState.footer, defaultMidpoint),
        response_schema: formState.systemResponseSchema,
        assistantId: assistantId || id as Id<"assistants">,
        name: formState.name,
        model: "gpt-4o-mini",
      }
      if (assistant?.openai_assistant_id) {
        const response = await fetch(`${convexSiteUrl}/assistant/update`, {
          method: "POST",
          body: JSON.stringify(requestBody),
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`,
          },
        });
        setShowInProgressAlert(false);
        if (response.status === 200) {
          setSuccess(true);
          setAlertMessage("Assistant updated successfully!");
          setShowAlert(true);
        } else {
          setSuccess(false);
          setAlertMessage("Something went wrong! Check the logs!");
          setShowAlert(true);
        }
      } else {
        const response = await fetch(`${convexSiteUrl}/assistant/create`, {
          method: "POST",
          body: JSON.stringify(requestBody),
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`,
          },
        });
        setShowInProgressAlert(false);
        if (response.status === 200) {
          setSuccess(true);
          setAlertMessage("Assistant created successfully!");
          setShowAlert(true);
        } else {
          setSuccess(false);
          setAlertMessage("Something went wrong! Check the logs!");
          setShowAlert(true);
        }
      }
      setShowInProgressAlert(false);
      setSuccess(true);
      setAlertMessage("Assistant updated successfully!");
      setShowAlert(true);
      
      // Clear localStorage cache and trigger form reload from database
      localStorage.removeItem(`assistant-form-${assistantId || 'new'}`);
      hasLoadedAssistant.current = false; // Reset so forceReload can reload the data
      setForceReload(true);
    } catch (error) {
      setShowInProgressAlert(false);
      setAlertMessage('Error updating assistant. Please try again.');
      setSuccess(false);
      setShowAlert(true);
    }
  };

  const handleCloseAlert = () => {
    setShowAlert(false);
    setSuccess(false);
    if (newAssistantId) {
      navigate(`../${newAssistantId}/`);
    }
  }

  const handleClear = () => {
    setRequest('');
    setGeneratedContent('');
    setGeneratedContentId(null);
  }

  const handleGenerate = async () => {
    const contentId = await generateContent({
      request,
      context: JSON.stringify({
        name: formState.name,
        display_name: formState.name,
        type: formState.type,
        greeting: formState.greeting,
        multiple_choice: formState.multipleChoice,
        prompt: formState.prompt,
        system_response_schema: formState.systemResponseSchema,
        header: formState.header,
        footer: formState.footer,
        description: formState.description,
        temperature: formState.temperature,
        is_core: formState.isCore,
        show_exit_button: formState.showExitButton,
        exit_button_caption: formState.exitButtonCaption,
        show_your_chat_button: formState.showYourChatButton,
        your_chat_button_caption: formState.yourChatButtonCaption,
        multiple_choice_questions: formState.multipleChoiceQuestions,
        multiple_choice_sign_off: formState.multipleChoiceSignOff,
        use_scoring: formState.useScoring,
        use_summary: formState.useSummary,
        show_upload_resume_button: formState.showUploadResumeButton,
        upload_resume_button_caption: formState.uploadResumeButtonCaption,
      }),
    });
    setGeneratedContentId(contentId);
    setGeneratedContent('');
  }

  return (
    <div className="flex">
      <section className="min-h-screen bg-background pt-24 px-6 flex flex-col w-full md:w-2/3">
        <Button color="light" className="mt-2 mb-2 w-80" onClick={() => navigate('../')}>
          Go back
        </Button>
        <form onSubmit={handleSubmit} className='pr-4'>
          <Button color="light" className="mt-6 mb-8 w-80" type="submit">
            {assistantId ? 'Update Assistant' : 'Add Assistant'}
          </Button>
          <Field>
            <Label>Assistant Name</Label>
            <Input
              required
              name="name"
              value={formState.name}
              onChange={(e) => updateFormState({ ...formState, name: e.target.value })}
            />
          </Field>

          <Field className="mt-4">
          <Label>Assistant Display Name</Label>
            <Input
              required
              name="display_name"
              value={formState.display_name}
              onChange={(e) => updateFormState({ ...formState, display_name: e.target.value })}
            />
          </Field>

          <Field className="mt-4">
            <Label>Assistant Type</Label>
            <Select
              required
              value={formState.type}
              onChange={(e) => updateFormState({ ...formState, type: e.target.value as Id<"assistant_types"> })}
            >
              <option value="">Select a type...</option>
              {assistantTypes.map(assistantType => (
                <option key={assistantType._id} value={assistantType._id}>
                  {assistantType.name}
                </option>
              ))}
            </Select>
          </Field>

          <Field className="flex flex-col mt-2 mb-2">
            <Label className="text-lg font-semibold">Is Core</Label>
            <Text className="text-gray-600 mb-2">Enable if this assistant should be marked as a core assistant</Text>
            <Switch name="isCore" checked={formState.isCore} onClick={(e) => updateFormState({ ...formState, isCore: !formState.isCore })} />
          </Field>

          <Field>
            <Label className="text-lg font-semibold">Description</Label>
            <Text className="text-gray-600 mb-2">The description of the assistant</Text>
            <Textarea required name="description" value={formState.description} onChange={(e) => updateFormState({ ...formState, description: e.target.value })} />
          </Field>

          <Field className="flex flex-col mt-2 mb-2">
            <Label className="text-lg font-semibold">Is Draft</Label>
            <Text className="text-gray-600 mb-2">Enable if this assistant should be marked as a draft</Text>
            <Switch name="isDraft" checked={formState.isDraft} onClick={(e) => updateFormState({ ...formState, isDraft: !formState.isDraft })} />
          </Field>

          <Field className="flex flex-col mt-2 mb-2">
            <Label className="text-lg font-semibold">🛠️ Enable Session Tools</Label>
            <Text className="text-gray-600 mb-2">
              Enable AI tools for this session: capture insights, save action items, search knowledge base, and more.
              Recommended for Reframe sessions (mindset & reflection work).
            </Text>
            <Switch
              name="useTools"
              checked={formState.useTools}
              onClick={(e) => updateFormState({ ...formState, useTools: !formState.useTools })}
            />
          </Field>

          <Field className="mt-4">
            <Label className="text-lg font-semibold">⭐ Points Reward</Label>
            <Text className="text-gray-600 mb-2">
              Amount of points the user earns for completing this session. Default is 50 points.
            </Text>
            <Input
              type="number"
              min="0"
              name="xpReward"
              value={formState.xpReward}
              onChange={(e) => updateFormState({ ...formState, xpReward: parseInt(e.target.value) || 0 })}
            />
          </Field>

          <Field className="mt-4">
            <Label className="text-lg font-semibold flex items-center gap-1.5"><AcornIcon size={22} className="text-primary inline-block" /> Acorn Reward</Label>
            <Text className="text-gray-600 mb-2">
              Amount of acorns the user earns for completing this session. Default is 5 acorns.
            </Text>
            <Input
              type="number"
              min="0"
              name="acornReward"
              value={formState.acornReward}
              onChange={(e) => updateFormState({ ...formState, acornReward: parseInt(e.target.value) || 0 })}
            />
          </Field>

          <Field>
            <Label className="text-lg font-semibold">Greeting</Label>
            <Text className="text-gray-600 mb-2">The initial message the assistant will use to start conversations</Text>
            <Textarea required name="greeting" value={formState.greeting} onChange={(e) => updateFormState({ ...formState, greeting: e.target.value })} />
          </Field>

          <Field>
            <Label className="text-lg font-semibold">Temperature</Label>
            <Text className="text-gray-600 mb-2">The temperature of the assistant</Text>
            <Input required name="temperature" value={formState.temperature} onChange={(e) => updateFormState({ ...formState, temperature: parseFloat(e.target.value) })} />
          </Field>

          <Field className="flex flex-col mt-2 mb-2">
            <Label className="text-lg font-semibold">Multiple choice</Label>
            <Text className="text-gray-600 mb-2">Enable if the assistant should provide multiple choice responses</Text>
            <Switch name="multipleChoice" checked={formState.multipleChoice} onClick={(e) => updateFormState({ ...formState, multipleChoice: !formState.multipleChoice })} />
          </Field>

          {formState.multipleChoice && (
            <MultipleChoiceQuestionsList questions={formState.multipleChoiceQuestions} setQuestions={(questions) => updateFormState({ ...formState, multipleChoiceQuestions: questions })} />
          )}

          {formState.multipleChoice && (
            <Field>
              <Label>Sign off message(only for multiple choice)</Label>
              <Textarea required name="multipleChoiceSignOff" value={formState.multipleChoiceSignOff} onChange={(e) => updateFormState({ ...formState, multipleChoiceSignOff: e.target.value })} />
            </Field>
          )}

          {/* <Field className="flex flex-col mt-2 mb-2">
            <Label className="text-lg font-semibold">Show exit button</Label>
            <Text className="text-gray-600 mb-2">Enable if the assistant should show an exit button when interaction is complete</Text>
            <Switch name="showExitButton" checked={formState.showExitButton} onClick={(e) => updateFormState({ ...formState, showExitButton: !formState.showExitButton })} />
          </Field>

          <Field>
            <Label>Exit button caption</Label>
            <Input
              disabled={!formState.showExitButton}
              name="exitButtonCaption"
              value={formState.exitButtonCaption}
              onChange={(e) => updateFormState({ ...formState, exitButtonCaption: e.target.value })}
            />
          </Field> */}

          {/* <Field className="flex flex-col mt-2 mb-2">
            <Label className="text-lg font-semibold">Show "your chat" button</Label>
            <Text className="text-gray-600 mb-2">Enable if the assistant should show a button to go to "your chat" when interaction is complete</Text>
            <Switch name="showYourChatButton" checked={formState.showYourChatButton} onClick={(e) => updateFormState({ ...formState, showYourChatButton: !formState.showYourChatButton })} />
          </Field>

          <Field>
            <Label>"Your chat" button caption</Label>
            <Input
              disabled={!formState.showYourChatButton}
              name="yourChatButtonCaption"
              value={formState.yourChatButtonCaption}
              onChange={(e) => updateFormState({ ...formState, yourChatButtonCaption: e.target.value })}
            />
          </Field> */}

          {/* <Field className="flex flex-col mt-2 mb-2">
            <Label className="text-lg font-semibold">Use scoring</Label>
            <Text className="text-gray-600 mb-2">Enable if the assistant should use scoring</Text>
            <Switch name="useScoring" checked={formState.useScoring} onClick={(e) => updateFormState({ ...formState, useScoring: !formState.useScoring })} />
          </Field>

          <Field className="flex flex-col mt-2 mb-2">
            <Label className="text-lg font-semibold">Generate summary and extract metadata</Label>
            <Text className="text-gray-600 mb-2">Enable if the assistant should use summary</Text>
            <Switch name="useSummary" checked={formState.useSummary} onClick={(e) => updateFormState({ ...formState, useSummary: !formState.useSummary })} />
          </Field>

          <Field className="flex flex-col mt-2 mb-2">
            <Label className="text-lg font-semibold">Show upload resume button</Label>
            <Text className="text-gray-600 mb-2">Enable if the assistant should show an upload resume button</Text>
            <Switch name="showUploadResumeButton" checked={formState.showUploadResumeButton} onClick={(e) => updateFormState({ ...formState, showUploadResumeButton: !formState.showUploadResumeButton })} />
          </Field>

          <Field>
            <Label>Upload resume button caption</Label>
            <Input
              disabled={!formState.showUploadResumeButton}
              name="uploadResumeButtonCaption"
              value={formState.uploadResumeButtonCaption}
              onChange={(e) => updateFormState({ ...formState, uploadResumeButtonCaption: e.target.value })}
            />
          </Field> */}
          
          {!formState.multipleChoice && <div className="bg-gray-50 p-6 rounded-lg mt-10">
            <Text className="font-semibold mb-4">System Prompt Configuration (Total length: <strong>{formState.header?.length + formState.footer?.length + formState.prompt?.length || 0}</strong> characters)</Text>
            <Text className="text-gray-600 mb-6">Configure how the assistant behaves and responds</Text>

            <FieldGroup className="space-y-6">
              <Field>
                <Label className="text-lg font-semibold">Prompt Header:</Label>
                <Text className="text-gray-600">Prompt header that will be used to create/update the assistant</Text>
                <Textarea required name="header" value={formState.header} onChange={(e) => updateFormState({ ...formState, header: e.target.value })} rows={5} />
                <Text className="mt-2 text-gray-600">Total length: <strong>{formState.header?.length || 0}</strong> characters</Text>
              </Field>

              <Field>
                <Label className="text-lg font-semibold">Prompt Midpoint:</Label>
                <Text className="text-gray-600">Prompt midpoint that will be used to create/update the assistant</Text>
                <Textarea required name="midpoint" value={formState.midpoint} onChange={(e) => updateFormState({ ...formState, midpoint: e.target.value })} rows={5} />
                <Text className="mt-2 text-gray-600">Total length: <strong>{formState.midpoint?.length || 0}</strong> characters</Text>
              </Field>
              
              <Field>
                <Label className="text-lg font-semibold">Prompt:</Label>
                <Text className="text-gray-600">The prompt that will be used to create/update the assistant</Text>
                <Textarea required name="prompt" value={formState.prompt} onChange={(e) => updateFormState({ ...formState, prompt: e.target.value })} rows={30} />
                <Text className="mt-2 text-gray-600">Total length: <strong>{formState.prompt?.length || 0}</strong> characters</Text>
              </Field>

              <Field>
                <Label className="text-lg font-semibold">Prompt Footer:</Label>
                <Text className="text-gray-600">Prompt footer that will be used to create/update the assistant</Text>
                <Textarea required name="footer" value={formState.footer} onChange={(e) => updateFormState({ ...formState, footer: e.target.value })} rows={5} />
                <Text className="mt-2 text-gray-600">Total length: <strong>{formState.footer?.length || 0}</strong> characters</Text>
              </Field>

              <Field>
                <Label className="text-lg font-semibold">Response schema</Label>
                <Text className="text-gray-600 mb-2">The response schema that will be used to create/update the assistant</Text>
                <Textarea required name="systemResponseSchema" value={formState.systemResponseSchema} onChange={(e) => updateFormState({ ...formState, systemResponseSchema: e.target.value })} rows={60} />
              </Field>
            </FieldGroup>
          </div>
          }
        </form>
        {assistantId ? <ChatSessionsList assistantId={assistantId as Id<"assistants">} /> : ''}
      </section>

      <div className="fixed top-0 right-0 w-1/3 h-screen p-4 bg-white shadow-lg border-l hidden md:block">
        <div className="flex flex-col h-full space-y-4">
          <Textarea
            placeholder="Generate with AI..."
            className="w-full"
            value={request}
            onChange={(e) => setRequest(e.target.value)}
            rows={10}
          />
          {generatedContentId && !generatedContent && (
            <div className="flex justify-center items-center h-full">
              <Spinner className="w-16 h-16" />
            </div>
          )}
          {generatedContentId && generatedContent && (  
            <Textarea
              placeholder="Response..."
              className="flex-1 w-full resize-none"
              readOnly={true}
              value={generatedContent}
            />
          )}

          <Button color="light" className="mt-6 mb-8 w-80" type="button" onClick={() => handleGenerate()}>
            Generate
          </Button>

          <Button color="light" className="mt-6 mb-8 w-80" type="button" onClick={() => handleClear()}>
            Clear
          </Button>
        </div>
      </div>

      <Alert open={showAlert} onClose={handleCloseAlert} size="lg">
        <AlertTitle>Notification</AlertTitle>
        <AlertDescription>{alertMessage}</AlertDescription>
        <AlertActions>
          <Button plain onClick={handleCloseAlert}>
            Ok
          </Button>
        </AlertActions>
      </Alert>
      <Alert open={showInProgressAlert} onClose={() => setShowInProgressAlert(false)} size="lg">
        <AlertTitle>Notification</AlertTitle>
        <AlertDescription>The assistant is being updated...</AlertDescription>
      </Alert>
    </div>
  );
};
